import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { CButton, CNav, CNavItem, CTabContent, CTabPane } from '@coreui/react-pro';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import { callApi } from '../../../../../../apiCaller';
import { isAuth } from '../../../../../../helpers/auth';
import {
     addTimezoneLocal,
     asyncLocalStorage,
     checkProductHasSeat,
     cloneDeep,
     formatEventSameDate,
     formatEventSingleDateTime,
     generateId,
     getLabelTimeZone,
     getListTimeZonesMoment,
     getTimeZoneGuess,
     sortArrayByProperty,
     toastError,
     tryParseJSON,
} from '../../../../../../utils';
import {
     API_GUEST_SUBSCRIBER_ADD_CART,
     API_GUEST_SUBSCRIBER_DETAIL_CART,
     API_GUEST_SUBSCRIBER_PRODUCT_TYPE,
     API_GUEST_SUBSCRIBER_UPDATE_CART,
     CART_TYPES,
     FALSY_VALUES,
     GUEST_PATH,
     LOCAL_CART,
     LOCAL_COUPON,
     LOCAL_COUPON_VOUCHERS,
     LOCAL_LICENSEE,
     LOCAL_ORG_USER,
     LOCAL_TIME_ZONE,
     LOCAL_TYPE_OF_PURCHASE,
     POPUP_SELECT_EVENT,
     POPUP_TYPE,
     PRODUCT_TYPES_REQUIRE_EVENT,
     STATUS,
     SUBSCRIBER_PATH,
     TAB_SELECT_PURCHASE,
     TRAINING_PRODUCT_TYPES,
     USER_ROLES,
} from '../../../../../../constants';
import images from '../../../../../../assets/images/index';

import { SelectSearchField } from '../../../../../General/Forms';
import NoYesPopup from '../../../../../General/Popups/NoYesPopup';
import TooltipWithIcon from '../../../../../General/Tooltips/TooltipWithIcon';

import { fetchSellingGroupDataDetail } from '../SellingDetails';
import PersonalTrainingTab from './PersonalTrainingTab';
import SelectAttachedEvent from './SelectAttachedEvent';
import TeamTrainingTab from './TeamTrainingTab';

import { setTimezone } from '../../actions';
import { actionClearCartCouponVouchers, actionSetCart } from '../../../Cart/actions';
import { fetchGuestContentManagements } from '../../../../../../containers/guest/action';
import { fetchActiveSeatListRequest } from '../../../../Subscriber/GlobalStates/Seat/actions';
import { fetchManagedOrganizationsRequest } from '../../../../Organizations/ManagedOrganizations/actions';
import { setProductTypes } from '../../../../Subscriber/GlobalStates/ProductType/actions';
import { setShow } from '../../../../../../actions/general';
import { fetchAwaitingOrderItemsRequest } from '../../../Checkout/action';
import { PopupContent } from '../../../../../General/Popups';
import { collectECEvent_addToCart_onPageProduct } from '../../../../../../utils/collect-ecommerce-event';

dayjs.extend(utc);
dayjs.extend(timezone);

export const TrainingProductCardContext = React.createContext({});

export const selectEventName = (eventLabel = '', shortDescription, remainingSeats = '') => {
     let seatAvailable = '';
     if (remainingSeats > 0) {
          if (remainingSeats === 1) {
               seatAvailable = remainingSeats + ' seat available';
          } else {
               seatAvailable = remainingSeats + ' seats available';
          }
     }

     return (
          <h6 className="select-event-name mb-0">
               {eventLabel}
               {seatAvailable && <span className="number-available-seat"> ({seatAvailable})</span>}
               {shortDescription?.trim() && (
                    <>
                         <br />
                         <em dangerouslySetInnerHTML={{ __html: shortDescription }} />
                    </>
               )}
          </h6>
     );
};

const TrainingProductCard = () => {
     const dispatch = useDispatch();
     const navigate = useNavigate();
     const slug = useParams().slug;

     const user = useSelector((state) => state.subscriber.user);
     const show = useSelector((state) => state.general.show);
     const licensees = useSelector((state) => state.subscriber.licensees);
     const sellingGroups = useSelector((state) => state.subscriber.sellingGroups);
     const productTypes = useSelector((state) => state.subscriber.productTypes);
     const managedOrganizations = useSelector((state) => state.subscriber.managedOrganizations);
     const listActiveSeatByUser = useSelector((state) => state.subscriber.listActiveSeatByUser);
     const contentManagements = useSelector((state) => state.subscriber.contentManagements);
     const products = useSelector((state) => state.subscriber.products.list);
     const sellingGroupFilterSelectedItems = useSelector((state) => state.subscriber.sellingGroupFilterSelected.items);

     const userRole = user?.role;
     const adminRole = isAuth() && (userRole === USER_ROLES.SYSTEM_ADMIN || userRole === USER_ROLES.SYSTEM_ADMIN_LOWER);

     const [disableCartButton, setDisableCartButton] = useState(adminRole);
     const [product, setProduct] = useState();
     const [disableCart, setDisableCart] = useState(false);
     const [listProducts, setListProducts] = useState(null);
     const [listProductCart, setListProductCart] = useState([]);
     const [openTimeZone, setOpenTimeZone] = useState(false);
     const [cartTypeRemovePopup, setCartTypeRemovePopup] = useState();
     const [purchaseTypeTab, setPurchaseTypeTab] = useState();
     const [listBundleExist, setListBundleExist] = useState(false);

     let contentPopup = {
          headline: '',
          body: '',
     };

     let actionShowErrorPopup = false;

     const userLicenseeId = user && licensees ? licensees.find((item) => item.country.includes(user.country))?.id : null;

     const orgUser = JSON.parse(localStorage.getItem(LOCAL_ORG_USER));

     const localLicensee = JSON.parse(localStorage.getItem(LOCAL_LICENSEE));

     const currentLicenseeId = userLicenseeId || localLicensee?.value;

     const timezoneUser = localStorage.getItem(LOCAL_TIME_ZONE);

     const fetchDataContentManagements = () => {
          if (!contentManagements.length) {
               dispatch(fetchGuestContentManagements());
          }
     };

     useEffect(fetchDataContentManagements, []); // eslint-disable-line react-hooks/exhaustive-deps

     const popupAddProductToCartTitle = contentManagements.find((content) => content.key === 'popupAddProductToCartTitle');
     const popupAddProductsToCartTitle = contentManagements.find((content) => content.key === 'popupAddProductsToCartTitle');
     const popupAddProductToCartContent = contentManagements.find((content) => content.key === 'popupAddProductToCartContent');
     const popupClearCartChangeTeamTitle = contentManagements.find((content) => content.key === 'popupClearCartChangeTeamTitle');
     const popupClearCartChangeTeamSubTitle = contentManagements.find((content) => content.key === 'popupClearCartChangeTeamSubTitle');
     const popupClearCartChangeTeamContentTooltip = contentManagements.find((content) => content.key === 'popupClearCartChangeTeamContentTooltip');
     const popupClearCartTitle = contentManagements.find((content) => content.key === 'popupClearCartTitle');
     const popupClearCartContent = contentManagements.find((content) => content.key === 'popupClearCartContent');
     const popupClearTheseCartTitle = contentManagements.find((content) => content.key === 'popupClearTheseCartTitle');
     const popupClearTheseCartContent = contentManagements.find((content) => content.key === 'popupClearTheseCartContent');
     const popupClearSomeCartTitle = contentManagements.find((content) => content.key === 'popupClearSomeCartTitle');
     const popupClearSomeCartContent = contentManagements.find((content) => content.key === 'popupClearSomeCartContent');
     const popupClearTheseCartTitleProductBundle = contentManagements.find((content) => content.key === 'popupClearTheseCartTitleProductBundle');
     const popupClearTheseCartContentProductBundle = contentManagements.find((content) => content.key === 'popupClearTheseCartContentProductBundle');

     const cartLocal = JSON.parse(localStorage.getItem(LOCAL_CART));

     const couponStorage = JSON.parse(localStorage.getItem(LOCAL_COUPON));
     const couponVouchersStorage = JSON.parse(localStorage.getItem(LOCAL_COUPON_VOUCHERS)) || [];

     const typeOfPurchase = localStorage.getItem(LOCAL_TYPE_OF_PURCHASE);

     const timezoneLocal = localStorage.getItem(LOCAL_TIME_ZONE) || getTimeZoneGuess();

     const orgIdDefault = managedOrganizations && managedOrganizations.length === 1 ? managedOrganizations[0].organizationId : null;

     const listProductTypeRequireEvent = productTypes?.filter((item) => PRODUCT_TYPES_REQUIRE_EVENT.includes(item.trainingProductType)) ?? null;
     const listTimezones = getListTimeZonesMoment();
     const [option, setOption] = useState(typeOfPurchase || '');
     const [cartLocalStorage, setCartLocalStorage] = useState(cartLocal);
     const [contentErrorPopup, setContentErrorPopup] = useState(contentPopup);
     const [timezoneValue, setTimeZoneValue] = useState({ label: getLabelTimeZone(timezoneLocal), value: timezoneLocal });

     const individualCartType = cartLocalStorage?.type === CART_TYPES.INDIVIDUAL;

     const productsData = sellingGroups
          ?.find((item) => item.slug === slug)
          ?.products?.filter(
               (pro) =>
                    pro.status === STATUS.ENABLE &&
                    (!currentLicenseeId || pro.licenseeId === currentLicenseeId) &&
                    pro.purchaseType !== CART_TYPES.INVISIBLE
          );

     const changeOption = (optionChoose) => {
          setOption(optionChoose);
          setDisableCart(false);

          setProduct({});
          setListProductCart([]);

          const newSellingGroupsDataDetail = fetchSellingGroupDataDetail(sellingGroups, optionChoose);
          const newSelling = newSellingGroupsDataDetail.find((item) => item.slug === slug);
          const personalTab = optionChoose === TAB_SELECT_PURCHASE.PERSONAL_TRAINING;
          let minSeatPurchase = 1;
          const newProducts = newSelling?.products
               ?.filter((item) => {
                    return item.status === STATUS.ENABLE && (!currentLicenseeId || item.licenseeId === currentLicenseeId);
               })
               ?.map((item) => {
                    if (personalTab) {
                         minSeatPurchase = item.individualMin;
                    } else {
                         minSeatPurchase = item.organizationMin;
                    }

                    return {
                         ...item,
                         selected: false,
                         quantityCart: minSeatPurchase,
                    };
               });

          setListProducts(newProducts);
     };

     const setOptionBasedOnCartType = () => {
          localStorage.setItem(LOCAL_TYPE_OF_PURCHASE, '');

          if (cartLocalStorage) {
               if (typeOfPurchase === TAB_SELECT_PURCHASE.PERSONAL_TRAINING || cartLocalStorage.type === CART_TYPES.INDIVIDUAL) {
                    changeOption(TAB_SELECT_PURCHASE.PERSONAL_TRAINING);
               }

               if (typeOfPurchase === TAB_SELECT_PURCHASE.TEAM_TRAINING || cartLocalStorage.type === CART_TYPES.ORGANIZATION) {
                    changeOption(TAB_SELECT_PURCHASE.TEAM_TRAINING);
               }

               return;
          }

          if (typeOfPurchase === TAB_SELECT_PURCHASE.TEAM_TRAINING || orgUser) {
               changeOption(TAB_SELECT_PURCHASE.TEAM_TRAINING);
          } else {
               changeOption(TAB_SELECT_PURCHASE.PERSONAL_TRAINING);
          }
     };

     const setTabBasedOnPurchaseType = () => {
          const productLength = productsData?.length;

          if (productLength && productsData.filter((pro) => pro.purchaseType === CART_TYPES.INDIVIDUAL).length === productLength) {
               setPurchaseTypeTab(CART_TYPES.INDIVIDUAL);

               if (cartLocalStorage?.type === CART_TYPES.ORGANIZATION) {
                    navigate(SUBSCRIBER_PATH.SELLING_GROUPS, { replace: true });
               }
          } else if (productLength && productsData.filter((pro) => pro.purchaseType === CART_TYPES.ORGANIZATION).length === productLength) {
               setPurchaseTypeTab(CART_TYPES.ORGANIZATION);

               if (cartLocalStorage?.type === CART_TYPES.INDIVIDUAL) {
                    navigate(SUBSCRIBER_PATH.SELLING_GROUPS, { replace: true });
               }
          } else {
               setPurchaseTypeTab(CART_TYPES.BOTH);
          }
     };

     const fetchData = () => {
          if (sellingGroups) {
               const newSellingGroupsDataDetail = fetchSellingGroupDataDetail(sellingGroups, option);
               const newSelling = newSellingGroupsDataDetail.find((item) => item.slug === slug);
               const cartType = cartLocalStorage?.type === CART_TYPES.INDIVIDUAL;
               let minSeatPurchase = 1;
               const newProducts = newSelling?.products
                    ?.filter((item) => item.status === STATUS.ENABLE && (!currentLicenseeId || item.licenseeId === currentLicenseeId))
                    ?.map((item) => {
                         if (cartType) {
                              minSeatPurchase = item.individualMin;
                         } else {
                              minSeatPurchase = item.organizationMin;
                         }
                         return {
                              ...item,
                              selected: false,
                              quantityCart: minSeatPurchase,
                              listEventSelected: [],
                         };
                    });
               setListProducts(newProducts);
               setOptionBasedOnCartType();
               setTabBasedOnPurchaseType();
          }

          if (!productTypes) {
               callApi(API_GUEST_SUBSCRIBER_PRODUCT_TYPE, 'GET').then((response) => {
                    if (response.status === 200) {
                         dispatch(setProductTypes(response.data));
                    } else {
                         toastError(response);
                    }
               });
          }

          if (!managedOrganizations && isAuth()) {
               dispatch(fetchManagedOrganizationsRequest());
          }
     };

     const fetchUserHasSeat = () => {
          if (user) {
               dispatch(fetchActiveSeatListRequest(user.id));
          }
     };

     const fetchAwaitingOrderItems = () => {
          if (user) {
               dispatch(fetchAwaitingOrderItemsRequest());
          }
     };

     useEffect(() => {
          fetchData();
          fetchUserHasSeat();
          fetchAwaitingOrderItems();
     }, [cartLocalStorage, sellingGroups]); // eslint-disable-line react-hooks/exhaustive-deps

     // Handle change tab select purchase team training with selling group filter
     useEffect(() => {
          if (purchaseTypeTab !== CART_TYPES.BOTH) return;

          let isTrainingForIndividualFilter = false;
          let isTrainingForOrganizationFilter = false;
          sellingGroupFilterSelectedItems.forEach((item) => {
               if (item.value === 'trainingFor_individual') {
                    isTrainingForIndividualFilter = true;
               }
               if (item.value === 'trainingFor_organization') {
                    isTrainingForOrganizationFilter = true;
               }
          });
          if (isTrainingForIndividualFilter && isTrainingForOrganizationFilter) {
               return;
          }

          if (isTrainingForIndividualFilter && option !== TAB_SELECT_PURCHASE.PERSONAL_TRAINING) {
               changeOption(TAB_SELECT_PURCHASE.PERSONAL_TRAINING);
          }

          if (isTrainingForOrganizationFilter && option !== TAB_SELECT_PURCHASE.TEAM_TRAINING) {
               changeOption(TAB_SELECT_PURCHASE.TEAM_TRAINING);
          }
     }, [purchaseTypeTab, sellingGroupFilterSelectedItems]); // eslint-disable-line react-hooks/exhaustive-deps

     const setListEventsProduct = (productSetEvent, newListProducts) => {
          if (productSetEvent.events?.length) {
               const listEventOptions = [];

               productSetEvent.events
                    .sort((a, b) => {
                         if (a.startAt === null || b.startAt === null) {
                              return new Date(b.startAt) - new Date(a.startAt);
                         }

                         return new Date(a.startAt) - new Date(b.startAt);
                    })
                    .forEach((event) => {
                         const timezoneSelected = localStorage.getItem(LOCAL_TIME_ZONE) || getTimeZoneGuess();
                         const newStartAt = dayjs(event.startAt).tz(timezoneSelected);
                         const today = dayjs().tz(timezoneSelected);

                         if (today < newStartAt && !event.isPrivate && event.status === STATUS.ENABLE) {
                              const eventLabel = `${formatEventSameDate(event.startAt, event.endAt)}`;
                              if (event.remainingSeats > 0 || event.remainingSeats === null) {
                                   const checkHasEventSelected = productSetEvent.listEventSelected?.some((ev) => ev.value === event.id);
                                   const newRemainingSeats = event.remainingSeats || event.maxSeats || 0;

                                   listEventOptions.push({
                                        value: event.id,
                                        label: selectEventName(eventLabel, event.shortDescription, event.remainingSeats || event.maxSeats),
                                        remainingSeats: newRemainingSeats > 0 ? newRemainingSeats : 0,
                                        notShow: checkHasEventSelected || false, //hide event Selected
                                   });
                              }
                         }
                    });

               const hasCustom =
                    (option === TAB_SELECT_PURCHASE.PERSONAL_TRAINING && productSetEvent.customEventMinSeat <= 1) ||
                    option === TAB_SELECT_PURCHASE.TEAM_TRAINING;

               if (productSetEvent?.customEventEnable && hasCustom) {
                    listEventOptions.push({
                         value: 'custom',
                         label: (
                              <div className="custom-date">
                                   <span className="custom-date__name">{productSetEvent.customEventGenericPublicName}</span>
                                   {''}
                                   <span className="custom-date__seat">
                                        {'(' +
                                             'Minimum ' +
                                             +productSetEvent.customEventMinSeat +
                                             (productSetEvent.customEventMinSeat > 1 ? ' seats' : ' seat') +
                                             ' required)'}
                                   </span>
                              </div>
                         ),
                    });
               }

               const productIndex = newListProducts.findIndex((item) => item.id === productSetEvent.id);
               const newProducts = [...newListProducts];

               if (productIndex !== -1) {
                    newProducts[productIndex] = {
                         ...newProducts[productIndex],
                         listEvents: listEventOptions,
                    };
               }

               setListProducts(newProducts);

               if (!listEventOptions.length && !productSetEvent.pricingTiers) {
                    setDisableCart(true);
               }

               return;
          }

          const hasCustom =
               (option === TAB_SELECT_PURCHASE.PERSONAL_TRAINING && productSetEvent.customEventMinSeat <= 1) ||
               option === TAB_SELECT_PURCHASE.TEAM_TRAINING;

          if (productSetEvent?.customEventEnable && hasCustom) {
               const newListEventsCustom = [
                    {
                         value: 'custom',
                         label: (
                              <div className="custom-date">
                                   <span className="custom-date__name">{productSetEvent.customEventGenericPublicName}</span>
                                   {''}
                                   <span className="custom-date__seat">
                                        {'(' +
                                             productSetEvent.customEventMinSeat +
                                             (productSetEvent.customEventMinSeat > 1 ? ' seats' : ' seat') +
                                             ' minimum required)'}
                                   </span>
                              </div>
                         ),
                    },
               ];

               const productIndex = newListProducts.findIndex((item) => item.id === productSetEvent.id);
               const newProducts = [...newListProducts];

               if (productIndex !== -1) {
                    newProducts[productIndex] = {
                         ...newProducts[productIndex],
                         listEvents: newListEventsCustom,
                    };
               }

               setListProducts(newProducts);

               return;
          }

          setListProducts(newListProducts);
     };

     const setProductId = (productChoose) => {
          if (productChoose?.purchaseType !== CART_TYPES.INVISIBLE) {
               let newProduct = {
                    ...productChoose,
                    selected: !productChoose.selected,
                    listEventSelected: [],
               };
               const indexProduct = listProducts.findIndex((item) => item.id === newProduct.id);

               let newListProductCart = [...listProductCart];

               if (productChoose.selected) {
                    delete newProduct.listEventSelected;
               }

               if (productChoose.selected) {
                    newProduct = {
                         ...newProduct,
                         quantityCart: productChoose?.organizationMin || 1,
                    };
                    if (newProduct.listTierSelected?.length) {
                         newListProductCart = [...newListProductCart].map((item) => {
                              if (item.id === newProduct.id) {
                                   if (productChoose.selected) {
                                        delete item.listEventSelected;
                                   }
                                   item.selected = newProduct.selected;
                              }
                              return item;
                         });
                    } else {
                         const indexProductCart = newListProductCart.findIndex((item) => item.id === newProduct.id);
                         newListProductCart.splice(indexProductCart, 1);
                    }
               } else if (newListProductCart.some((prod) => prod.id === productChoose.id)) {
                    const indexProductCart = newListProductCart.findIndex((prod) => prod.id === productChoose.id);
                    newListProductCart[indexProductCart] = newProduct;
               } else {
                    newListProductCart.push(newProduct);
               }

               const newProducts = [...listProducts];
               newProducts[indexProduct] = newProduct;

               setListProductCart(newListProductCart);

               if (productChoose.selected && newListProductCart.length) {
                    const lastProduct = newListProductCart[newListProductCart.length - 1];

                    setProduct(lastProduct);
                    setListEventsProduct(lastProduct, newProducts);
               } else {
                    setProduct(newProduct);
                    setListEventsProduct(productChoose, newProducts);
               }

               return;
          }

          setProduct(productChoose);
          setListEventsProduct(productChoose, listProducts);
     };

     const setTierId = (productChoose, tierChoose) => {
          const newProducts = cloneDeep(listProducts);
          const checkHaveTierSelected = productChoose.listTierSelected?.find((tierSelected) => tierSelected.id === tierChoose.id);
          const newProduct = {
               ...productChoose,
               cloneEventId: generateId(),
               listTierSelected: productChoose.listTierSelected ? [...productChoose.listTierSelected] : [],
          };
          if (!checkHaveTierSelected) {
               newProduct.listTierSelected?.push({ ...tierChoose, quantity: productChoose.organizationMin });
          } else {
               const indexTier = newProduct.listTierSelected?.findIndex((item) => item.id === tierChoose.id);
               newProduct.listTierSelected?.splice(indexTier, 1);
          }

          const productIndex = newProducts.findIndex((item) => item.id === newProduct.id);

          if (productIndex !== -1) {
               newProducts[productIndex] = newProduct;
          }

          setListEventsProduct(productChoose, newProducts);
          let newListProductCart = cloneDeep(listProductCart);

          const indexProductCart = newListProductCart.findIndex((item) => item.id === productChoose.id);

          if (indexProductCart !== -1 && productChoose) {
               if (!newProduct.listTierSelected) {
                    newListProductCart = newListProductCart.filter((item) => item.id !== newProduct.id);
               } else {
                    if (
                         !newProduct.listTierSelected?.length &&
                         !newProduct.selected &&
                         (!newProduct.listEventSelected || !newProduct.listEventSelected?.length)
                    ) {
                         newListProductCart.splice(indexProductCart, 1);
                    } else {
                         newListProductCart[indexProductCart] = newProduct;
                    }
               }
          } else {
               newListProductCart.push(newProduct);
          }

          setListProductCart(newListProductCart);

          const listEventSelected = newListProductCart.find((itemCart) => itemCart.selected && itemCart.listEventSelected)?.listEventSelected;
          const listTierSelected = newListProductCart.find((itemCart) => itemCart.listTierSelected)?.listTierSelected;

          if (listEventSelected?.length <= 0 || listTierSelected?.length <= 0) {
               setDisableCart(true);
          } else {
               setDisableCart(false);
          }
     };

     const handleSubmitPopupRemoveCartItems = () => {
          addNewCart(cartTypeRemovePopup);
          dispatch(setShow(null));
          setListBundleExist(false);

          if (couponStorage) {
               localStorage.removeItem(LOCAL_COUPON);
          }

          if (couponVouchersStorage.length) {
               localStorage.removeItem(LOCAL_COUPON_VOUCHERS);
               dispatch(actionClearCartCouponVouchers());
          }

          if (option === TAB_SELECT_PURCHASE.PERSONAL_TRAINING) {
               localStorage.removeItem(LOCAL_ORG_USER);

               if (user) {
                    addTimezoneLocal(user.timezone);
               }
          }
     };

     const getCartById = (cartId) => {
          setTimeout(() => {
               callApi(API_GUEST_SUBSCRIBER_DETAIL_CART.replace(':id', cartId), 'GET').then((response) => {
                    if (response.status !== 200) {
                         toastError(response);
                         return;
                    }

                    const { user, organization, sessionId, ...newCart } = response.data;
                    setCartLocalStorage(newCart);

                    asyncLocalStorage.setItem(LOCAL_CART, JSON.stringify(newCart)).then(() => {
                         dispatch(setShow(actionShowErrorPopup ? POPUP_TYPE.ERROR_ADD_CART : POPUP_TYPE.ADDED_CART));
                         !adminRole && setDisableCartButton(false);

                         if (!actionShowErrorPopup) {
                              //collect eCom dataLayer's event - Add To Cart
                              collectECEvent_addToCart_onPageProduct({ addedProducts: listProductCart, user });
                         }
                    });
                    dispatch(actionSetCart(newCart));
               });
          }, 500);
     };

     const addNewCart = (typeCart) => {
          const cartItems = [];

          for (const item of listProductCart) {
               const isCreditSelected = item.selectedEventCredit && item.selectedEventCreditQuantity > 0;

               if (isCreditSelected) {
                    cartItems.push({
                         productId: item.id,
                         quantity: typeCart === CART_TYPES.INDIVIDUAL ? 1 : item.selectedEventCreditQuantity,
                         isCredit: true,
                    });
               }

               if (item.listEventSelected?.length && !isCreditSelected) {
                    for (const ev of item.listEventSelected) {
                         cartItems.push({
                              productId: item.id,
                              eventId: ev.value,
                              quantity: typeCart === CART_TYPES.INDIVIDUAL ? 1 : ev.quantityEvent,
                         });
                    }
               }

               if (item.listTierSelected?.length && item.productType.trainingProductType === TRAINING_PRODUCT_TYPES.LIVE_TRAINING) {
                    for (const tier of item.listTierSelected) {
                         if (tier?.quantity > 1) {
                              for (let i = 0; i < tier.quantity; i++) {
                                   cartItems.push({
                                        tierId: tier.id,
                                        name: tier.name,
                                        productId: item.id,
                                        quantity: 1,
                                        tierSeats: tier.seats,
                                   });
                              }
                         } else if (tier?.quantity === 1) {
                              cartItems.push({
                                   tierId: tier.id,
                                   name: tier.name,
                                   productId: item.id,
                                   quantity: 1,
                                   tierSeats: tier.seats,
                              });
                         }
                    }
               }

               if (item.productType.trainingProductType !== TRAINING_PRODUCT_TYPES.LIVE_TRAINING) {
                    cartItems.push({
                         productId: item.id,
                         quantity: typeCart === CART_TYPES.INDIVIDUAL ? 1 : item.quantityCart,
                    });
               }
          }

          const cartPayload = {
               userId: typeCart === CART_TYPES.INDIVIDUAL && user ? user.id : null,
               cartItems,
               type: typeCart,
               organizationId:
                    orgIdDefault && typeCart !== CART_TYPES.INDIVIDUAL
                         ? orgIdDefault
                         : orgUser && typeCart !== CART_TYPES.INDIVIDUAL
                         ? orgUser.organizationId
                         : null,
          };

          callApi(API_GUEST_SUBSCRIBER_ADD_CART, 'POST', cartPayload).then((response) => {
               if (response.status === 200) {
                    getCartById(response.data?.id, cartItems);
               } else {
                    setDisableCartButton(false);
                    toastError(response);
               }
          });
     };

     const addNewCartItem = (typeCart) => {
          const cartItemsLocal = cartLocalStorage.cartItems;
          let cartItems = cartItemsLocal?.length ? cartItemsLocal : [];
          let isAllowUpdateCartItem = true;

          for (const item of listProductCart) {
               const isLiveTraining = item.productType.trainingProductType === TRAINING_PRODUCT_TYPES.LIVE_TRAINING;
               const isCreditSelected = item.selectedEventCredit && item.selectedEventCreditQuantity > 0;

               if (isCreditSelected) {
                    let qty = Number(item.selectedEventCreditQuantity || 0);
                    const cartItem = cartItems?.find((cartItem) => cartItem.productId === item.id && cartItem.isCredit);

                    if (cartItem) {
                         qty = qty + Number(cartItem.quantity);
                         const newQuantity = typeCart === CART_TYPES.INDIVIDUAL ? 1 : qty;
                         const cartItemIndex = cartItems.findIndex((cartItem) => cartItem.productId === item.id && cartItem.isCredit);
                         if (cartItemIndex !== -1) {
                              cartItems[cartItemIndex].quantity = newQuantity;
                         }
                    } else {
                         cartItems.push({
                              productId: item.id,
                              quantity: typeCart === CART_TYPES.INDIVIDUAL ? 1 : item.selectedEventCreditQuantity,
                              isCredit: true,
                         });
                    }
               }

               if (item.listEventSelected?.length && !isCreditSelected) {
                    for (const ev of item.listEventSelected) {
                         let qty = ev.quantityEvent;
                         const cartItem = cartItemsLocal?.find(
                              (cartItemLocal) => cartItemLocal.productId === item.id && cartItemLocal.eventId === ev.value
                         );

                         if (cartItem) {
                              qty = Number(ev.quantityEvent) + Number(cartItem.quantity);
                              const newQuantity = typeCart === CART_TYPES.INDIVIDUAL ? 1 : qty;
                              const cartItemIndex = cartItems.findIndex((item) => item.eventId === ev.value);

                              if (cartItemIndex !== -1) {
                                   cartItems[cartItemIndex].quantity = newQuantity;
                              }
                         } else {
                              const newQuantity = typeCart === CART_TYPES.INDIVIDUAL ? 1 : qty;

                              cartItems.push({
                                   productId: item.id,
                                   quantity: newQuantity,
                                   eventId: ev.value,
                              });
                         }
                    }
               }

               if (item.listTierSelected?.length) {
                    for (const tier of item.listTierSelected) {
                         if (tier?.quantity > 0) {
                              const cartItemIndex = cartItems.findIndex((cartItem) => cartItem.tierId === tier.id);
                              const cartItemExist = !!cartItems[cartItemIndex];

                              const quantity = cartItems[cartItemIndex]?.quantity || 1;
                              for (let i = 0; i < tier.quantity; i++) {
                                   const count = cartItemExist && i === 0 ? 1 : 0; // Only get first index
                                   const quantityNewCart = count === 0 ? tier.quantity : item?.organizationMin + quantity;

                                   cartItems.push({
                                        tierId: tier.id,
                                        name: tier.name,
                                        productId: item.id,
                                        quantity: cartItemExist ? quantity + count : quantityNewCart,
                                        tierSeats: tier.seats,
                                   });
                              }
                         }
                    }
               }

               if (!isLiveTraining) {
                    let qty = Number(item.quantityCart);
                    const cartItem = cartItems?.find((cartItem) => cartItem.productId === item.id);

                    if (cartItem) {
                         qty = qty + Number(cartItem.quantity);
                         const newQuantity = typeCart === CART_TYPES.INDIVIDUAL ? 1 : qty;
                         const cartItemIndex = cartItems.findIndex((cartItem) => cartItem.productId === item.id);

                         if (cartItemIndex !== -1) {
                              cartItems[cartItemIndex].quantity = newQuantity;
                         }
                    } else {
                         cartItems.push({
                              productId: item.id,
                              quantity: typeCart === CART_TYPES.INDIVIDUAL ? 1 : item.quantityCart,
                         });
                    }
               }

               if (item.organizationMax === -1) continue;

               for (let i = 0; i < cartItems.length; i++) {
                    const cartItem = cartItems[i];
                    const selectedEvent = item.listEventSelected.find((event) => event.value === cartItem.eventId);
                    const remainingSeat = selectedEvent?.remainingSeat;
                    const product = products.find((product) => product.id === cartItem.productId);
                    const isBundle = product.productType.trainingProductType === TRAINING_PRODUCT_TYPES.BUNDLE;

                    const maxQty = Number(remainingSeat || item.organizationMax || 0);
                    const currentQty = !isBundle ? Number(cartItem.quantity || 0) : 0;
                    const additionalEventQty = Number(!cartItem.isCredit ? selectedEvent?.quantityEvent : 0);
                    const additionalCreditQty = Number(cartItem.isCredit && item.selectedEventCredit ? item.selectedEventCreditQuantity : 0);
                    const additionalQty = cartItem.isCredit ? additionalCreditQty : additionalEventQty;

                    if (currentQty > maxQty) {
                         isAllowUpdateCartItem = false;
                         cartItem.quantity -= additionalQty;

                         toast.error(
                              `Can not purchase for ${currentQty} ${currentQty > 1 ? 'seats' : 'seat'} for course ${
                                   item.publishName
                              }. Exceeding the number of seats allowed for one-time purchase (${maxQty} ${maxQty > 1 ? 'seats' : 'seat'})`
                         );
                         break;
                    }
               }

               if (!isAllowUpdateCartItem) break;
          }

          if (!isAllowUpdateCartItem) {
               setDisableCartButton(false);
               return;
          }

          const cartPayload = {
               id: cartLocalStorage.id,
               cartItems,
               type: typeCart,
               switchIndividual: true,
          };

          callApi(API_GUEST_SUBSCRIBER_UPDATE_CART, 'PUT', cartPayload).then((response) => {
               if (response.status === 200) {
                    getCartById(cartLocalStorage.id, cartItems);
               } else {
                    setDisableCartButton(false);
                    toastError(response);
               }
          });
     };

     const addToCartAgain = (typeCart) => {
          if (cartLocalStorage.type === CART_TYPES.INDIVIDUAL) {
               const physicalProductItems = listProductCart.filter(
                    (prod) => prod.productType.trainingProductType === TRAINING_PRODUCT_TYPES.PHYSICAL_PRODUCT
               );
               const allItemsArePhysicalProd = physicalProductItems.length === listProductCart.length;

               if (option === TAB_SELECT_PURCHASE.TEAM_TRAINING || allItemsArePhysicalProd) {
                    if (cartLocalStorage.cartItems?.length) {
                         if (allItemsArePhysicalProd) {
                              addNewCartItem(typeCart);
                         } else {
                              dispatch(setShow(POPUP_TYPE.REMOVE_CART));
                              setCartTypeRemovePopup(CART_TYPES.ORGANIZATION);
                         }
                    } else {
                         addNewCart(CART_TYPES.ORGANIZATION);
                    }
               } else {
                    actionShowErrorPopup = true;
                    addNewCartItem(typeCart);
               }

               return;
          }

          if (option === TAB_SELECT_PURCHASE.TEAM_TRAINING) {
               addNewCartItem(typeCart);
          } else {
               if (cartLocalStorage.cartItems?.length) {
                    dispatch(setShow(POPUP_TYPE.REMOVE_CART));
                    setCartTypeRemovePopup(CART_TYPES.INDIVIDUAL);
               } else {
                    addNewCart(CART_TYPES.INDIVIDUAL);
               }
          }
     };

     const listProductBundleExist = () => {
          if (option === TAB_SELECT_PURCHASE.PERSONAL_TRAINING) {
               if (!cartLocalStorage?.cartItems?.length) {
                    return false;
               }

               // loop product add to cart
               for (let i = 0; i < listProductCart.length; i++) {
                    const item = listProductCart[i];
                    const productBundles = tryParseJSON(item?.listBundleProduct) || [];

                    // if product is bundle
                    if (productBundles.length) {
                         // check product add to cart with list product bundle in Cart
                         const isCheckProductCart = cartLocalStorage.cartItems.some((cartItem) => {
                              const productId = cartItem.productId;
                              const productCartItem = products.find((pro) => pro.id === productId);
                              const trainingProductType = productCartItem?.productType?.trainingProductType;
                              const isLiveTraining = trainingProductType === TRAINING_PRODUCT_TYPES.LIVE_TRAINING;
                              const purchaseEventCredit = isLiveTraining && productCartItem.allowPurchaseEventCredit;
                              const allowProductType = [TRAINING_PRODUCT_TYPES.PHYSICAL_PRODUCT, TRAINING_PRODUCT_TYPES.CE_CREDITS].includes(
                                   trainingProductType
                              );
                              if (allowProductType || purchaseEventCredit) {
                                   return false;
                              }

                              const productBundlesCartItem = tryParseJSON(productCartItem?.listBundleProduct) || [];
                              if (productBundlesCartItem.length) {
                                   //if product in cart is bundle
                                   const addBundleAgain = cartLocalStorage?.cartItems?.some((cartLocal) => cartLocal.productId === item.id);

                                   return productBundlesCartItem.some(({ id }) => {
                                        const productCartItem = products.find((pro) => pro.id === id);
                                        const trainingProductType = productCartItem?.productType?.trainingProductType;
                                        const isLiveTraining = trainingProductType === TRAINING_PRODUCT_TYPES.LIVE_TRAINING;
                                        const purchaseEventCredit = isLiveTraining && productCartItem.allowPurchaseEventCredit;
                                        const allowProductType = [
                                             TRAINING_PRODUCT_TYPES.PHYSICAL_PRODUCT,
                                             TRAINING_PRODUCT_TYPES.CE_CREDITS,
                                        ].includes(trainingProductType);

                                        if (allowProductType || purchaseEventCredit || addBundleAgain) {
                                             return false;
                                        }

                                        return productBundles.some((pro) => pro.id === id);
                                   });
                              } else {
                                   // if product in cart is not bundle
                                   return productBundles.some((pro) => pro.id === productId);
                              }
                         });
                         if (isCheckProductCart) {
                              return { isCheckProductCart: true, flagCartItem: false };
                         }
                    } else {
                         // check product add to cart with list product in Cart
                         for (let j = 0; j < cartLocalStorage.cartItems.length; j++) {
                              const cartItem = cartLocalStorage.cartItems[j];

                              const cartProductId = cartItem.productId;
                              const productBundleCart = products.find(({ id }) => id === cartProductId);
                              const productBundlesCart = tryParseJSON(productBundleCart?.listBundleProduct) || [];

                              if (productBundlesCart.length) {
                                   const isCheckBundleCart = productBundlesCart.some(({ id }) => {
                                        const productAddAgain = products.find((pro) => pro.id === id);
                                        const trainingProductType = productAddAgain?.productType?.trainingProductType;
                                        const isLiveTraining = trainingProductType === TRAINING_PRODUCT_TYPES.LIVE_TRAINING;
                                        const purchaseEventCredit = isLiveTraining && productAddAgain.allowPurchaseEventCredit;
                                        const allowProductType = [
                                             TRAINING_PRODUCT_TYPES.PHYSICAL_PRODUCT,
                                             TRAINING_PRODUCT_TYPES.CE_CREDITS,
                                        ].includes(trainingProductType);

                                        if (allowProductType || purchaseEventCredit) {
                                             return false;
                                        }

                                        return item.id === id;
                                   });

                                   if (isCheckBundleCart) {
                                        return { isCheckBundleCart: true, flagCartItem: true };
                                   }
                              }
                         }
                    }
               }
               return false;
          }
     };

     const checkDuplicateProducts = () => {
          let bundleName1 = '';

          if (option !== TAB_SELECT_PURCHASE.PERSONAL_TRAINING) {
               return {
                    isDuplicate: false,
               };
          }

          for (let i = 0; i < listProductCart.length; i++) {
               const product = listProductCart[i];
               bundleName1 = product.genericPublicName;

               // check product bundle with list product add cart
               const productBundlesItem = tryParseJSON(product.listBundleProduct) || [];
               if (productBundlesItem.length) {
                    for (let j = 0; j < productBundlesItem.length; j++) {
                         let bundleName2 = '';
                         const productBundle = productBundlesItem[j];

                         const productBundleDetail = products.find((pro) => pro.id === productBundle.id);
                         const productName = productBundleDetail.genericPublicName;

                         const trainingProductType = productBundleDetail?.productType?.trainingProductType;
                         const isLiveTraining = trainingProductType === TRAINING_PRODUCT_TYPES.LIVE_TRAINING;
                         const purchaseEventCredit = isLiveTraining && productBundleDetail.allowPurchaseEventCredit;
                         const allowProductType = [TRAINING_PRODUCT_TYPES.PHYSICAL_PRODUCT, TRAINING_PRODUCT_TYPES.CE_CREDITS].includes(
                              trainingProductType
                         );

                         if (allowProductType || purchaseEventCredit) {
                              continue;
                         }

                         // check product with list product add cart
                         const newListProductCart = listProductCart.filter((pro) => pro.id !== product.id);

                         const isDuplicate = newListProductCart.some((newProduct) => {
                              const trainingProductType = newProduct?.productType?.trainingProductType;
                              const isLiveTraining = trainingProductType === TRAINING_PRODUCT_TYPES.LIVE_TRAINING;
                              const isBundle = trainingProductType === TRAINING_PRODUCT_TYPES.BUNDLE;
                              const purchaseEventCredit = isLiveTraining && newProduct.allowPurchaseEventCredit;
                              const allowProductType = [TRAINING_PRODUCT_TYPES.PHYSICAL_PRODUCT, TRAINING_PRODUCT_TYPES.CE_CREDITS].includes(
                                   trainingProductType
                              );

                              if (allowProductType || purchaseEventCredit) {
                                   return false;
                              }

                              if (isBundle) {
                                   //if product in list cart is bundle
                                   const listBundleProduct = tryParseJSON(newProduct.listBundleProduct) || [];
                                   const result = listBundleProduct.some((pro) => pro.id === productBundle.id);
                                   if (result) {
                                        bundleName2 = newProduct.genericPublicName;
                                   }
                                   return result;
                              } else {
                                   //if product in list cart is not bundle
                                   return newProduct.id === productBundle.id;
                              }
                         });

                         if (isDuplicate) {
                              return { isDuplicate: true, bundleName1, productName, bundleName2 };
                         }
                    }
               }
          }

          return { isDuplicate: false };
     };

     const addToCart = (typeCart) => {
          const checkEventAlreadyOwned = listProductCart.some((item) => {
               return item.listEventSelected?.some((event) => checkProductHasSeat(listActiveSeatByUser, item.id, event.value));
          });

          if (checkEventAlreadyOwned && option === TAB_SELECT_PURCHASE.PERSONAL_TRAINING) {
               toast.error('You have an active seat, so you cannot purchase it again.');
               setDisableCart(true);
               return;
          }

          // check list product selected  against each other
          const checkDuplicate = checkDuplicateProducts();
          if (checkDuplicate.isDuplicate) {
               dispatch(setShow(POPUP_TYPE.ERROR_DUPLICATE_PRODUCT_ADD_CART));
               const bundleName1 = checkDuplicate.bundleName1;
               const productName = checkDuplicate.productName;
               const bundleName2 = checkDuplicate.bundleName2;
               contentPopup = {
                    headline: popupClearTheseCartTitleProductBundle?.value,
                    body: `You are unable to finalize this purchase as the ${bundleName1} you recently included consists of the ${productName} that are already present in the previously added ${
                         bundleName2 || ''
                    }`,
               };
               setContentErrorPopup(contentPopup);

               return;
          }

          //check list product selected with list product in cart

          const checkListProductBundleExist = listProductBundleExist();
          if (checkListProductBundleExist) {
               const popupBundle = !checkListProductBundleExist.flagCartItem;
               dispatch(setShow(POPUP_TYPE.ERROR_ADD_CART));
               contentPopup = {
                    headline: popupBundle ? popupClearTheseCartTitleProductBundle?.value : popupClearTheseCartTitle?.value,
                    body: popupBundle ? popupClearTheseCartContentProductBundle?.value : popupClearTheseCartContent?.value,
               };

               setContentErrorPopup(contentPopup);
               setListBundleExist(true);
               return;
          }

          setDisableCartButton(true);
          if (cartLocalStorage === null) {
               addNewCart(typeCart);

               if (option === TAB_SELECT_PURCHASE.PERSONAL_TRAINING) {
                    localStorage.removeItem(LOCAL_ORG_USER);
               }

               return;
          }

          const listTrainingDateExist = listProductCart.filter((item) => {
               if (!cartLocalStorage.cartItems?.length) {
                    return false;
               }

               // if (item.selectedEventCredit && item.selectedEventCreditQuantity > 0) {
               //      return cartLocalStorage.cartItems.some(({ productId, isCredit }) => item.id === productId && isCredit);
               // }

               if (item.listEventSelected?.length) {
                    return cartLocalStorage.cartItems.some(({ eventId, productId }) =>
                         item?.listEventSelected.some((ev) => eventId && ev.value === eventId && item.id === productId)
                    );
               }

               if (item.listTierSelected?.length) {
                    return cartLocalStorage.cartItems.some(({ tierId, productId }) =>
                         item?.listTierSelected.some((tier) => tierId && tier.id === tierId && item.id === productId)
                    );
               }

               return false;
          });

          const checkProductAdded = listProductCart.filter((item) => {
               if (!cartLocalStorage.cartItems?.length) {
                    return false;
               }

               const isLiveTraining = item.productType.trainingProductType !== TRAINING_PRODUCT_TYPES.LIVE_TRAINING;
               const isPhysical = item.productType.trainingProductType !== TRAINING_PRODUCT_TYPES.PHYSICAL_PRODUCT;
               const allowType = isLiveTraining && isPhysical;

               return cartLocalStorage.cartItems.some(({ productId }) => productId === item?.id && allowType);
          });

          if (listTrainingDateExist.length || (!listTrainingDateExist.length && checkProductAdded.length)) {
               addToCartAgain(typeCart);
          }

          if (!listTrainingDateExist.length && !checkProductAdded.length) {
               if (cartLocalStorage.type === CART_TYPES.INDIVIDUAL) {
                    if (option === TAB_SELECT_PURCHASE.TEAM_TRAINING) {
                         if (cartLocalStorage.cartItems?.length > 0) {
                              dispatch(setShow(POPUP_TYPE.REMOVE_CART));
                              setCartTypeRemovePopup(CART_TYPES.ORGANIZATION);
                         } else {
                              addNewCart(CART_TYPES.ORGANIZATION);
                              localStorage.removeItem(LOCAL_COUPON);
                              localStorage.removeItem(LOCAL_COUPON_VOUCHERS);
                         }
                    } else {
                         addNewCartItem(typeCart);
                    }
               } else {
                    if (option === TAB_SELECT_PURCHASE.TEAM_TRAINING) {
                         addNewCartItem(typeCart);
                    } else {
                         if (cartLocalStorage.cartItems?.length > 0) {
                              dispatch(setShow(POPUP_TYPE.REMOVE_CART));
                              setCartTypeRemovePopup(CART_TYPES.INDIVIDUAL);
                         } else {
                              addNewCart(CART_TYPES.INDIVIDUAL);
                              localStorage.removeItem(LOCAL_COUPON);
                              localStorage.removeItem(LOCAL_COUPON_VOUCHERS);
                              localStorage.removeItem(LOCAL_ORG_USER);
                         }
                    }
               }
          }

          if (listProductCart.length === checkProductAdded.length || listProductCart.length === listTrainingDateExist.length) {
               if (checkProductAdded.length === 1 || listTrainingDateExist.length === 1) {
                    contentPopup = {
                         headline: popupClearCartTitle?.value,
                         body: popupClearCartContent?.value,
                    };
               } else if (checkProductAdded.length > 1 || listTrainingDateExist.length > 1) {
                    contentPopup = {
                         headline: popupClearTheseCartTitle?.value,
                         body: popupClearTheseCartContent?.value,
                    };
               }
          } else {
               contentPopup = {
                    headline: popupClearSomeCartTitle?.value,
                    body: popupClearSomeCartContent?.value,
               };
          }

          setContentErrorPopup(contentPopup);
     };

     const handleTimeZone = (e) => {
          const value = e.value;

          if (FALSY_VALUES.includes(value)) {
               return;
          }

          dispatch(setTimezone(e.value));
          setTimeZoneValue({
               label: getLabelTimeZone(value),
               value,
          });

          localStorage.setItem(LOCAL_TIME_ZONE, value);

          const newListProductCart = listProductCart.map((cart) => {
               const listEvents = cart.events;
               let listEventSelected = [...cart.listEventSelected];

               if (listEventSelected.length) {
                    listEventSelected = cart.listEventSelected.map((event) => {
                         const eventFound = listEvents.find((e) => e.id === event.value);
                         const eventLabel = eventFound ? `${formatEventSingleDateTime(eventFound.startAt)}` : event.label;

                         return {
                              ...event,
                              label: selectEventName(eventLabel, event.shortDescription, event.remainingSeats || event.maxSeats),
                         };
                    });
               }

               let newListEvents = !cart.listEvents ? listProducts.find((item) => item.id === cart.id)?.listEvents || [] : [...cart.listEvents];

               if (newListEvents?.length) {
                    newListEvents = newListEvents.map((event) => {
                         const eventFound = listEvents.find((e) => e.id === event.value);
                         const eventLabel = eventFound ? `${formatEventSingleDateTime(eventFound.startAt)}` : event.label;

                         return {
                              ...event,
                              label: selectEventName(eventLabel, event.shortDescription, event.remainingSeats || event.maxSeats),
                         };
                    });
               }

               return {
                    ...cart,
                    listEventSelected,
                    listEvents: newListEvents,
               };
          });

          setListProductCart(newListProductCart);

          const newListProducts = listProducts.map((pro) => {
               const productFound = newListProductCart.find((p) => p.id === pro.id);

               if (!productFound) {
                    return pro;
               }

               return {
                    ...pro,
                    listEventSelected: productFound.listEventSelected,
                    listEvents: productFound.listEvents,
               };
          });

          setListProducts(newListProducts);
     };

     const checkShowTimezone = listProducts?.some((pro) => pro.productType.trainingProductType === TRAINING_PRODUCT_TYPES.LIVE_TRAINING);

     if (listProducts) {
          sortArrayByProperty(listProducts, 'sortingOder');
     }

     const SubtitleClearCartPopup = () => {
          return (
               <>
                    {popupClearCartChangeTeamSubTitle?.value}
                    <TooltipWithIcon
                         className="tooltip-info"
                         content={popupClearCartChangeTeamContentTooltip?.value}
                         trigger="click"
                         srcIcon={images.IconFAInforDark}
                    />{' '}
                    <a href="/">Why</a>
                    <span>?</span>
               </>
          );
     };

     const handleAcceptAddedCartPopupButtonClick = () => {
          navigate(GUEST_PATH.CART);
          dispatch(setShow(null));
     };

     const handleCloseAddedCartPopupButtonClick = () => {
          setListBundleExist(false);
          if (cartLocal) {
               setCartLocalStorage(cartLocal);
          }

          setListProductCart([]);
          fetchData();
          dispatch(setShow(null));
          changeOption(TAB_SELECT_PURCHASE.PERSONAL_TRAINING);
     };
     const checkListProductBundleExist = listProductBundleExist();
     const isErrorAddCart = listBundleExist && !checkListProductBundleExist?.flagCartItem;

     return (
          <div className="training-product-card explore-product-card">
               {show === POPUP_SELECT_EVENT && (
                    <SelectAttachedEvent
                         listProductCart={listProductCart}
                         product={product}
                         listProducts={listProducts}
                         option={option}
                         setListProducts={setListProducts}
                         setListProductCart={setListProductCart}
                    />
               )}

               {show === POPUP_TYPE.ADDED_CART && !listBundleExist && (
                    <NoYesPopup
                         showPopup={true}
                         title={listProductCart.length > 1 ? popupAddProductsToCartTitle?.value : popupAddProductToCartTitle?.value}
                         subTitle={popupAddProductToCartContent?.value}
                         popupIcon={images.iconAddCart}
                         showIconClose={false}
                         noButtonText="Continue Shopping"
                         yesButtonText="View My Cart"
                         yesButtonColor="primary"
                         noButtonColor="primary"
                         className="modal-product-cart"
                         onClose={handleCloseAddedCartPopupButtonClick}
                         onAccept={handleAcceptAddedCartPopupButtonClick}
                    />
               )}

               {show === POPUP_TYPE.ERROR_DUPLICATE_PRODUCT_ADD_CART && (
                    <PopupContent
                         showPopup={true}
                         title={contentErrorPopup.headline}
                         subTitle={contentErrorPopup.body}
                         showIconClose={true}
                         popupIcon={images.iconCartClear}
                         buttonText="Cancel"
                         titleColor="danger"
                         buttonColor="outline-secondary button-popup-content"
                         className="modal-product-cart popup-duplicate-cart"
                         onClose={handleCloseAddedCartPopupButtonClick}
                         onAccept={handleCloseAddedCartPopupButtonClick}
                    />
               )}

               {show === POPUP_TYPE.ERROR_ADD_CART && (
                    <NoYesPopup
                         showPopup={true}
                         title={contentErrorPopup.headline}
                         popupIcon={images.iconCartClear}
                         showIconClose={false}
                         yesButtonColor="danger"
                         noButtonText={isErrorAddCart ? 'Cancel' : 'Continue Shopping'}
                         yesButtonText={isErrorAddCart ? 'Proceed' : 'View My Cart'}
                         className="modal-product-cart modal-product-cart-danger"
                         onClose={handleCloseAddedCartPopupButtonClick}
                         onAccept={isErrorAddCart ? handleSubmitPopupRemoveCartItems : handleAcceptAddedCartPopupButtonClick}
                    >
                         <p dangerouslySetInnerHTML={{ __html: contentErrorPopup.body }} />
                    </NoYesPopup>
               )}

               {show === POPUP_TYPE.REMOVE_CART && (
                    <NoYesPopup
                         showPopup={true}
                         title={popupClearCartChangeTeamTitle?.value}
                         subTitle={<SubtitleClearCartPopup />}
                         showIconClose={false}
                         popupIcon={images.iconCartClear}
                         yesButtonColor="danger"
                         noButtonText="Cancel"
                         yesButtonText="Proceed"
                         className="modal-product-cart modal-product-cart-danger"
                         onClose={() => {
                              dispatch(setShow(null));
                              setDisableCartButton(false);
                         }}
                         onAccept={handleSubmitPopupRemoveCartItems}
                    >
                         <p className="clear-cart-text">
                              Your cart currently contains {individualCartType ? 'Personal Training products' : 'Team Training products'} and if you
                              proceed to add these products, we will clear your cart and your order type will change to a{' '}
                              {individualCartType ? 'Team Training products' : 'Personal Training products'} order.{' '}
                         </p>
                    </NoYesPopup>
               )}

               <div className="training-product-card-body">
                    {purchaseTypeTab === CART_TYPES.BOTH ? (
                         <CNav variant="pills" role="tablist">
                              <CNavItem>
                                   <CButton
                                        className="nav-link"
                                        onClick={() => changeOption(TAB_SELECT_PURCHASE.PERSONAL_TRAINING)}
                                        color={`${option === TAB_SELECT_PURCHASE.PERSONAL_TRAINING ? 'primary' : 'secondary'}`}
                                   >
                                        PERSONAL TRAINING
                                   </CButton>
                              </CNavItem>
                              <CNavItem>
                                   <CButton
                                        className="nav-link"
                                        onClick={() => changeOption(TAB_SELECT_PURCHASE.TEAM_TRAINING)}
                                        color={`${option === TAB_SELECT_PURCHASE.TEAM_TRAINING ? 'primary' : 'secondary'}`}
                                   >
                                        TEAM TRAINING
                                   </CButton>
                              </CNavItem>
                         </CNav>
                    ) : purchaseTypeTab === CART_TYPES.INDIVIDUAL ? (
                         <CNav variant="pills" role="tablist">
                              <CNavItem>
                                   <CButton
                                        className="nav-link"
                                        color={`${option === TAB_SELECT_PURCHASE.PERSONAL_TRAINING ? 'primary' : 'secondary'} disabled`}
                                   >
                                        PERSONAL TRAINING
                                   </CButton>
                              </CNavItem>
                         </CNav>
                    ) : (
                         <CNav variant="pills" role="tablist">
                              <CNavItem>
                                   <CButton
                                        className="nav-link"
                                        color={`${option === TAB_SELECT_PURCHASE.TEAM_TRAINING ? 'primary' : 'secondary'} disabled`}
                                   >
                                        TEAM TRAINING
                                   </CButton>
                              </CNavItem>
                         </CNav>
                    )}

                    {listProducts && (
                         <TrainingProductCardContext.Provider
                              value={{
                                   cartLocalStorage,
                                   disableCart,
                                   listProductCart,
                                   listProducts,
                                   listProductTypeRequireEvent,
                                   disableCartButton,
                                   option,
                                   product,
                                   addToCart,
                                   setDisableCart,
                                   setListEventsProduct,
                                   setListProductCart,
                                   setListProducts,
                                   setProduct,
                                   setProductId,
                                   setTierId,
                              }}
                         >
                              <CTabContent>
                                   <CTabPane
                                        role="tabpanel"
                                        aria-labelledby="personal-training-tab"
                                        visible={option === TAB_SELECT_PURCHASE.PERSONAL_TRAINING}
                                   >
                                        <PersonalTrainingTab />
                                   </CTabPane>
                                   <CTabPane
                                        role="tabpanel"
                                        aria-labelledby="team-training-tab"
                                        visible={option === TAB_SELECT_PURCHASE.TEAM_TRAINING}
                                   >
                                        <TeamTrainingTab />
                                   </CTabPane>
                                   {checkShowTimezone && (
                                        <div className="training-product-card-footer footer-card-product">
                                             {isAuth() ? (
                                                  <>
                                                       {user && (
                                                            <div className="training-time-zone">
                                                                 <p>
                                                                      All dates and times are shown in{' '}
                                                                      <strong>
                                                                           {getLabelTimeZone(
                                                                                timezoneUser || orgUser?.organization.timezone || user.timezone
                                                                           )}
                                                                      </strong>
                                                                      . This is the timezone set on your CAMS training account.
                                                                 </p>
                                                            </div>
                                                       )}
                                                  </>
                                             ) : (
                                                  <div className="training-time-zone">
                                                       <p>
                                                            All dates and times are shown in <strong>{timezoneValue.label}</strong>. To view training
                                                            dates and times in your local time, you may{' '}
                                                            <strong className="change-time-zone" onClick={() => setOpenTimeZone(!openTimeZone)}>
                                                                 change time zone
                                                            </strong>
                                                       </p>
                                                       {openTimeZone && (
                                                            <SelectSearchField
                                                                 placeholder="Select Time Zone..."
                                                                 options={listTimezones}
                                                                 selectInputType={false}
                                                                 onChange={handleTimeZone}
                                                                 value={timezoneValue}
                                                                 height
                                                            />
                                                       )}
                                                  </div>
                                             )}
                                        </div>
                                   )}
                              </CTabContent>
                         </TrainingProductCardContext.Provider>
                    )}
               </div>
          </div>
     );
};

export default TrainingProductCard;
